import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { combineLatest, of, throwError } from 'rxjs';
import {
  concatMap, distinctUntilChanged, takeUntil
} from 'rxjs/operators';

import { validateEmailsList } from 'app/constants';
import { Offer } from 'graphql/models/extended';
import { Account, Customer, ContactPerson, Branch } from 'graphql/models/generated';
import { DropdownItem } from 'models/dropdown.model';
import { OfferPreviewSettings } from 'models/offer-preview-settings';
import { OfferBase } from 'models/offer.model';
import { Template, TemplateType } from 'models/template.model';
import { BaseComponent } from 'shared/base/base.component';
import { AccountService } from 'shared/services/account.service';
import { CoreDataService } from 'shared/services/core-data.service';
import { OfferService } from 'shared/services/offer.service';
import { StoreService } from 'shared/services/store.service';
import { AccountTypeENUM } from 'models/account.model';

@Component({
  selector: 'app-offer-preview-send',
  templateUrl: './offer-preview-send.component.html',
  styleUrls: ['./offer-preview-send.component.scss']
})
export class OfferPreviewSendComponent extends BaseComponent implements OnInit {
  form!: UntypedFormGroup;

  offer!: OfferBase;

  currentAccount!: Account;

  offerTitle?: string;

  offerPreviewSettings!: OfferPreviewSettings;

  customer!: Customer;

  contactPerson!: ContactPerson | any;

  templates!: Template[];

  readonly templateType: TemplateType = TemplateType.EMAIL;

  formReady = false;

  pdfSrc = '';

  isDealer!: boolean;

  // RFP version sent by dealer (snkModifier)
  rfp!: Offer | undefined;

  currentBranch!: Branch;

  dropdownData: Record<string, any> = {
    dealers: [] as DropdownItem[]
  };

  searchOptions: DropdownItem[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private storeService: StoreService,
    private accountService: AccountService,
    private coreDataService: CoreDataService,
    private offerService: OfferService,
  ) { super(); }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this.storeService.setFormReady(false);

    combineLatest([
      this.storeService.getCurrentOffer(),
      this.storeService.getAccount(),
      this.offerService.getRfpVersion(),
      this.storeService.getCurrentAccountBranch()
    ])
      .pipe(
        concatMap(([currentOffer, currentAccount, rfp, branch]: [OfferBase, Account, Offer | undefined, Branch]) => {
          const canPreviewOffer = this.offerService.canPreviewOffer(currentOffer as Offer);
          if (!canPreviewOffer) {
            return throwError(() => new Error('User cannot preview offer!'));
          }
          this.offer = currentOffer;
          this.currentAccount = currentAccount;
          this.isDealer = currentAccount.accountType === AccountTypeENUM.DEALER;
          this.offerTitle = this.offerService.getCurrentOfferTitle(this.offer as Offer, this.isDealer);

          if(currentOffer.documentId === rfp?.documentId) {
            this.rfp = rfp;
          }

          this.currentBranch = branch;

          const customer$ = currentOffer.customerId
            ? this.coreDataService.getCustomer(currentOffer.customerId as string) : of({} as Customer);

          const contactPerson$ = currentOffer.contactPersonId
            ? this.coreDataService.getContactPerson(currentOffer.contactPersonId) : of({} as ContactPerson);

          const dealerAccounts$ = (!this.isDealer && currentOffer.documentId !== rfp?.documentId) && currentOffer.customerId
            ? this.accountService.getDealerAccounts(currentOffer.customerId) : of([] as Account[]);

          return combineLatest([
            this.storeService.getOfferPreview(),
            customer$,
            contactPerson$,
            dealerAccounts$,
            this.coreDataService.getTemplates(100, this.templateType)
          ]);
        }),
        takeUntil(this.destroyed$)
      ).subscribe(([
        offerPreviewSettings,
        customer,
        contactPerson,
        dealerAccounts,
        templates
      ]: [OfferPreviewSettings, Customer, ContactPerson, Account[], Template[]]) => {
        this.offerPreviewSettings = offerPreviewSettings;
        this.templates = templates || [];
        this.pdfSrc = offerPreviewSettings.pdfSrc || '';
        this.customer = customer || {};
        this.contactPerson = contactPerson || {};
        this.dropdownData.dealers = this.accountService.mapAccountsToDropdown(dealerAccounts);
        this.searchOptions = (this.dropdownData.dealers || []);

        this.setupForm();
      });
  }

  private setupForm(): void {
    const email = this.isDealer
      ? (this.contactPerson.email || this.customer.email || '')
      : ((this.rfp && this.rfp.snkModifier && this.rfp.snkModifier.username) || '');

    this.form = this.fb.group({
      email: [(email ? [email] : null), [validateEmailsList, Validators.required]],
      templates: [],
      text: ['', { updateOn: 'change' }]
    }, { updateOn: 'blur' });

    this.formReady = true;
    this.storeService.setFormReady(this.formReady);
    this.detectFormChanges();
  }

  detectFormChanges(): void {
    this.form.controls.templates.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((id: string) => {
        const template = this.templates.find((t: Template) => t.id === id);

        this.form.controls.text.setValue((template && template.text) || '');
      });
  }

  setPdfReady(isReady: boolean): void {
    if (!this.form) return;

    if (isReady) {
      this.form.enable({ emitEvent: false });
    } else {
      this.form.disable();
      (this.form.statusChanges as any).emit('INVALID');
    }
  }

  onAddCustom(value: any) {
    return { title: value, value: value };
  }

  onAddDropdown(value: any): void {
    this.form.controls.email.setValue([value], {emitEvent: true});
  }

  onCloseDropdown(data: any): void {
    if (typeof data !== 'string') {
      this.form.controls.email.setValue(data, { emitEvent: true });
    } else {
      const value: string = data.trim();
      if (value && value !== '') {
        const valuesList = value.replace(/[;,]/g, ',').split(",");

        this.form.controls.email.setValue(valuesList, {emitEvent: true});
      }
    }
  }

  getDropdownData(event?: string): void {
    let search = (event ? event.trim() : '');
    if (search !== '') {
      search = search.toLowerCase();
      this.searchOptions = this.dropdownData.dealers.filter((o: any) => (
        o.title.toLowerCase().indexOf(search) > -1)
        || (o.value.toLowerCase().indexOf(search) > -1)
      );
    } else {
      this.searchOptions = this.dropdownData.dealers;
    }
  }
}
